import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import * as Sentry from '@sentry/browser'
import { toast } from 'react-toastify'
import { Document, Page, pdfjs } from 'react-pdf'
import { saveAs } from 'file-saver'

import {
  Button,
  CircularProgress,
  Fab,
  FormControl,
  FormControlLabel,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
} from '@material-ui/core'
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined'

import { getFilesResponse, postFiles, postGenerateCsv } from 'services/apiRpg'
import {
  addCheckInDcbeAssets,
  addCheckInIrDeclarations,
  calculeTotal,
  getFileNameExtension,
} from 'utils/functions'
import { FILE_TYPE } from 'utils/constants'

import IrValues from './IrValues'
import IrReceiptValues from './IrReceiptValues'
import DcbeValues from './DcbeValues'
import DcbeReceiptValues from './DcbeReceiptValues'
import Container from './styles'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

const Home = () => {
  const userId = useSelector((state) => state.auth.sub)
  const apiKey = useSelector((state) => state.auth.api_key)

  const [dataRes, setDataRes] = useState(null)
  const [dataFiles, setDataFiles] = useState([])
  const [dataFilesReceipt, setDataFilesReceipt] = useState([])
  const [validTotal, setValidTotal] = useState(null)
  const [imgsBase64, setImgsBase64] = useState([])
  const [filesType, setFilesType] = useState(FILE_TYPE.IR)
  const [loading, setLoading] = useState(false)

  const handleFile = async (event) => {
    if (!_.isEmpty(dataRes)) {
      setImgsBase64([])
    }
    setDataRes(null)
    setDataFiles([])
    setValidTotal(null)

    const { files } = event.target
    const newFiles = []

    _.each(files, file => {
      // TODO: check if file is image or pdf
      const reader = new FileReader()

      reader.onloadend = () => {
        newFiles.push({
          name: file.name,
          base64: reader.result,
        })
        if (_.size(newFiles) === _.size(files)) {
          const filesUniq = _.differenceBy(newFiles, imgsBase64, 'base64')
          if (!_.isEmpty(filesUniq)) setImgsBase64(prevVal => ([ ...prevVal, ...filesUniq ]))
        }
      }

      reader.readAsDataURL(file)
    })
  }

  const handleDocumentPdfLoadSuccess = ({ numPages }, fileIndex) => {
    const newImgsBase64 = _.cloneDeep(imgsBase64)
    _.set(
      newImgsBase64,
      `${fileIndex}.pdfNumPages`,
      numPages,
    )

    setImgsBase64(newImgsBase64)
  }

  const handleDocumentPdfLoadError = (err) => {
    console.error(err.message)
    Sentry.captureException(err)
  }

  const handleFileDelete = (fileIndex) => {
    const newFiles = _.cloneDeep(imgsBase64)
    newFiles.splice(fileIndex, 1)
    setImgsBase64(newFiles)
  }

  const getFilesResult = (token) => {
    let res = null

    const intervalId = setInterval(async () => {
      try {
        res = await getFilesResponse(
          apiKey,
          userId,
          token,
        )
      } catch (err) {
        clearInterval(intervalId)
        console.error(err)
        Sentry.captureException(err)

        const messageError = _.get(err, 'response.data.msg_errors.0.msg') || 'Erro ao obter dados'
        toast.error(messageError)
        setLoading(false)
        return
      }

      if (res.status !== 200) return

      clearInterval(intervalId)

      const newDataRes = res.data

      let newDataFiles = _
        .chain(newDataRes)
        .get('response.files')
        .filter({ file_type: 'declaration' })
        .value()

      newDataFiles = _.get(newDataRes, 'files_type') === FILE_TYPE.DCBE
        ? addCheckInDcbeAssets(newDataFiles)
        : addCheckInIrDeclarations(newDataFiles)

      const newDataFilesReceipt = _
        .chain(newDataRes)
        .get('response.files')
        .filter({ file_type: 'receipt' })
        .value()

      setDataFiles(newDataFiles)
      setDataFilesReceipt(newDataFilesReceipt)
      setValidTotal(_.get(newDataRes, 'response.valid_total'))
      setDataRes(newDataRes)

      setLoading(false)
    }, 5000)
  }

  const handleExportCsv = async () => {
    const currFilesType = _.get(dataRes, 'files_type')
    const assetsField = currFilesType === FILE_TYPE.IR
      ? 'assets_declaration'
      : 'asset_details'

    const assets = _
    .chain(dataFiles)
    .filter(x => _.isArrayLikeObject(x[assetsField]))
    .map(x => x[assetsField])
    .flatten()
    .value()

    const data = {
      total_value: calculeTotal(currFilesType, dataFiles),
      total_liquid_value: calculeTotal(currFilesType, dataFiles, { filterChecked: true }),
      total_liquid_value_80_percent: calculeTotal(currFilesType, dataFiles, { filterChecked: true, percentage: 80 }),
      valid_total: validTotal,
      [assetsField]: assets,
    }

    let res = null

    try {
      res = await postGenerateCsv(
        apiKey,
        userId,
        currFilesType,
        data,
      )
    } catch (err) {
      console.error(err)
      Sentry.captureException(err)

      const messageError = _.get(err, 'response.data.msg_errors.0.msg') || 'Erro exportar CSV'
      toast.error(messageError)
      return
    }

    const blobText = new Blob([res.data], { type: 'text/csv;charset=utf-8' })
    saveAs(blobText, `${currFilesType}_exported.csv`)
  }

  const handleSubmit = async (files) => {
    if (_.isEmpty(files)) return
    setLoading(true)

    const filesTreated = _.map(files, (file) => {
      return {
        ...file,
        base64: file.base64.substr(file.base64.indexOf(',') + 1),
      }
    })

    setDataRes(null)
    setDataFiles([])
    setValidTotal(null)

    let res = null
    try {
      res = await postFiles(
        apiKey,
        userId,
        filesType,
        filesTreated,
      )
    } catch (err) {
      console.error(err)
      Sentry.captureException(err)
      setLoading(false)

      const messageError = _.get(err, 'response.data.msg_errors.0.msg') || 'Erro ao enviar arquivos'
      toast.error(messageError)
      return
    }

    getFilesResult(res.data.token)
  }

  const handleDeclarationCheck = (event, fileIndex, declarationIndex) => {
    const assetsField = _.get(dataRes, 'files_type') === FILE_TYPE.IR
      ? 'assets_declaration'
      : 'asset_details'

    const newDataFiles = _.cloneDeep(dataFiles)
    _.set(
      newDataFiles,
      `${fileIndex}.${assetsField}.${declarationIndex}.checked`,
      event.target.checked,
    )

    setDataFiles(newDataFiles)
  }

  const handleChangeFilesType = (event) => {
    setFilesType(event.target.value)
  }

  const renderFileTypeIr = () => {
    return (
      <React.Fragment>
        <IrReceiptValues
          filesData={dataFilesReceipt}
        />

        <IrValues
          filesData={dataFiles}
          validTotal={validTotal}
          onDeclarationCheck={handleDeclarationCheck}
          equityEvolution={_.get(dataRes, 'response.equity_evolution')}
        />
      </React.Fragment>
    )
  }

  const renderFileTypeDcbe = () => {
    return (
      <React.Fragment>
        <DcbeReceiptValues
          filesData={dataFilesReceipt}
        />

        <DcbeValues
          filesData={dataFiles}
          validTotal={validTotal}
          onDeclarationCheck={handleDeclarationCheck}
        />
      </React.Fragment>
    )
  }

  return (
    <Container>
      <div className="files-container">
        <div className="files-btns-container">
          <div className="btn-item">
            <Input
              accept="image/png,image/jpeg,image/jpg,application/pdf"
              inputProps={{ multiple: true }}
              onChange={handleFile}
              type="file"
            />
          </div>

          <FormControl component="fieldset">
            <FormLabel component="legend">Tipo</FormLabel>
            <RadioGroup
              aria-label="Tipo"
              name="tipo"
              row={true}
              value={filesType}
              onChange={handleChangeFilesType}
            >
              <FormControlLabel value="ir" label="IR" control={<Radio color="primary" />} />
              <FormControlLabel value="dcbe" label="DCBE" control={<Radio color="primary" />} />
            </RadioGroup>
          </FormControl>

          <div className="btn-item">
            <Button
              color="primary"
              disabled={_.isEmpty(imgsBase64)}
              onClick={() => handleSubmit(imgsBase64)}
              variant="contained"
            >
              Enviar
            </Button>
          </div>
        </div>

        <div className="files-files-container">
          <React.Fragment>
            {_.map(imgsBase64, (fileData, i) => (
              <div key={i.toString()}>
                <div className="img-btn-delete-container">
                  <Fab
                    size="small"
                    color="primary"
                    aria-label="add"
                    onClick={() => handleFileDelete(i)}
                  >
                    <DeleteOutlinedIcon />
                  </Fab>
                </div>
                {
                  getFileNameExtension(fileData.name) === 'pdf' ? (
                    <Document
                      file={fileData.base64}
                      onLoadSuccess={(data) => handleDocumentPdfLoadSuccess(data, i)}
                      onLoadError={handleDocumentPdfLoadError}
                    >
                      {_.map(new Array(fileData.pdfNumPages), (_val, indexPage) => (
                        <Page pageNumber={indexPage + 1} key={indexPage.toString()} className="PDF-Page" />
                      ))}
                    </Document>
                  ) : <img src={fileData.base64} alt="imagem" width="100%" />
                }
              </div>
            ))}
          </React.Fragment>
        </div>
      </div>

      <div className="content-container">
        <div className="btn-export-container">
          <Button
            disabled={_.isEmpty(dataRes)}
            onClick={() => handleExportCsv()}
            variant="contained"
          >
            Exportar para CSV
          </Button>
        </div>
        <div className="values-container">
          {
            loading
            ? <CircularProgress />
            : (
              !_.isEmpty(dataRes) && (
                <React.Fragment>
                  <h2 style={{ alignSelf: 'flex-start', marginBottom: 10, }}>
                    {`Tipo: ${_.get(dataRes, 'files_type')}`}
                  </h2>
                  {
                    _.get(dataRes, 'files_type') === FILE_TYPE.DCBE
                    ? renderFileTypeDcbe()
                    : renderFileTypeIr()
                  }
                </React.Fragment>
              )
            )
          }
        </div>
      </div>
    </Container>
  )
}

export default Home
